import {
  createSlice,
  PayloadAction,
  Selector
} from '@reduxjs/toolkit'

export interface Activity {
  [index: string]: string | boolean | [Activity] | undefined | any,
  title: string,
  type: string,
  participantKey: string,
  registeredParticipantsOnly: boolean,
  gradingEnabled: boolean,
  anonymous: boolean,
  inCompetition: boolean,
  showBranding: boolean,
  titleHidden?: boolean,
  polls?: [Activity]
}

export interface ResponsePageState {
  [index: string]: string | boolean | Activity | undefined,
  currentActivity?: Activity | undefined,
  pinnedActivity?: Activity | undefined,
  showPinned: boolean,
  showScreenNameEdit: boolean,
  forceRegister: boolean,
  skipRegister: boolean,
  outOfBandPending: boolean
}

const sliceName = 'responsePage'

export const selectCurrentActivity: Selector<unknown, Activity | undefined> = (state: any): Activity | undefined => state[sliceName].currentActivity
export const selectPinnedActivity: Selector<unknown, Activity | undefined> = (state: any): Activity | undefined => state[sliceName].pinnedActivity
export const selectShowPinned: Selector<unknown, boolean> = (state: any): boolean => state[sliceName].showPinned
export const selectSkippedRegister: Selector<unknown, boolean> = (state: any): boolean => state[sliceName].skipRegister
export const selectForcedRegister: Selector<unknown, boolean> = (state: any): boolean => state[sliceName].forceRegister

function buildLockedActivityRequiringRegistration (type: string, participantKey: string): Activity {
  return {
    title: 'Registration required',
    type,
    participantKey,
    registeredParticipantsOnly: true,
    gradingEnabled: false,
    anonymous: false,
    inCompetition: false,
    showBranding: false
  }
}

const selectors = {
  selectCurrentActivity,
  selectPinnedActivity,
  selectSkippedRegister
}
const thunks = {}
const initialState: ResponsePageState = {
  showPinned: false,
  showScreenNameEdit: false,
  forceRegister: false,
  skipRegister: false,
  outOfBandPending: false
}

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset () {
      return initialState
    },
    manuallyUpdated (state, { payload }: PayloadAction<ResponsePageState>) {
      for (const key of Object.keys(payload)) {
        state[key] = payload[key]
      }
    },
    setCurrentActivity (state, { payload }: PayloadAction<Activity | undefined>) {
      state.currentActivity = payload
    },
    setPinnedActivity (state, { payload }: PayloadAction<Activity | undefined>) {
      state.pinnedActivity = payload
    },
    setCurrentActivityLockedRequiringRegistration (state, { payload }: PayloadAction<{ type: string, participantKey: string }>) {
      state.currentActivity = buildLockedActivityRequiringRegistration(payload.type, payload.participantKey)
    },
    setPinnedActivityLockedRequiringRegistration (state, { payload }: PayloadAction<{ type: string, participantKey: string }>) {
      state.pinnedActivity = buildLockedActivityRequiringRegistration(payload.type, payload.participantKey)
    },
    togglePinned (state, { payload }: PayloadAction<boolean | undefined>) {
      state.showPinned = typeof payload === 'boolean' ? payload : !state.showPinned
    },
    toggleScreenNameEdit (state, { payload }: PayloadAction<boolean | undefined>) {
      state.showScreenNameEdit = typeof payload === 'boolean' ? payload : !state.showScreenNameEdit
    },
    toggleForceRegister (state, { payload }: PayloadAction<boolean | undefined>) {
      state.forceRegister = typeof payload === 'boolean' ? payload : !state.forceRegister
    },
    toggleSkipRegister (state, { payload }: PayloadAction<boolean | undefined>) {
      state.skipRegister = typeof payload === 'boolean' ? payload : !state.skipRegister
    },
    toggleOutOfBandPending (state, { payload }: PayloadAction<boolean | undefined>) {
      state.outOfBandPending = typeof payload === 'boolean' ? payload : !state.outOfBandPending
    }
  }
})

const sliceExport = {
  name: sliceName,
  reducer: slice.reducer,
  ...slice.actions,
  ...selectors,
  ...thunks
}

export { sliceExport as slice }
